import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { DeviceLockUnlockFilters, LockUnlockAction, ReportSortBy, ReportSortOrder } from '../api/types'

/**
 * Filter state for the device activity reports, kept in the URL query so a
 * dashboard card can deep-link a pre-filtered report and a filtered view
 * survives reload / share. Values read from the URL are validated; anything
 * unrecognised falls back to the default rather than reaching the server.
 *
 * Every change rewrites the whole query, so pagination (which is keyed on the
 * returned `resetKey`) goes back to page 1 whenever a filter changes.
 */

export const REPORT_PAGE_SIZES = [10, 20, 50, 100] as const
export const DEFAULT_REPORT_PAGE_SIZE = 20

/** Mirrors the BFF/core limits (DeviceReportFilter, application/reporting). */
export const MAX_DEVICE_ID_LENGTH = 200
export const MAX_OPERATOR_LENGTH = 100

export interface ReportFilterValues {
  fromDate: string
  toDate: string
  deviceId: string
  sortBy: ReportSortBy
  sortOrder: ReportSortOrder
  action: LockUnlockAction | ''
  operator: string
  pageSize: number
}

export type ReportFilterPatch = Partial<Omit<ReportFilterValues, 'pageSize'>>

const DEFAULTS: ReportFilterValues = {
  fromDate: '',
  toDate: '',
  deviceId: '',
  sortBy: 'date',
  sortOrder: 'desc',
  action: '',
  operator: '',
  pageSize: DEFAULT_REPORT_PAGE_SIZE,
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** YYYY-MM-DD that is a real calendar day (rejects 2026-02-30). */
export function isIsoDate(v: string): boolean {
  if (!DATE_RE.test(v)) return false
  const d = new Date(`${v}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v
}

/** `lockUnlock` enables the action/operator filters; other reports ignore them. */
export function parseReportFilters(sp: URLSearchParams, lockUnlock = false): ReportFilterValues {
  const date = (k: string) => {
    const v = sp.get(k)?.trim() ?? ''
    return isIsoDate(v) ? v : ''
  }
  const text = (k: string, max: number) => (sp.get(k)?.trim() ?? '').slice(0, max)
  const action = sp.get('action')?.toUpperCase()
  const pageSize = Number(sp.get('pageSize'))
  return {
    fromDate: date('fromDate'),
    toDate: date('toDate'),
    deviceId: text('deviceId', MAX_DEVICE_ID_LENGTH),
    sortBy: sp.get('sortBy') === 'deviceId' ? 'deviceId' : 'date',
    sortOrder: sp.get('sortOrder') === 'asc' ? 'asc' : 'desc',
    action: lockUnlock && (action === 'LOCK' || action === 'UNLOCK') ? action : '',
    operator: lockUnlock ? text('operator', MAX_OPERATOR_LENGTH) : '',
    pageSize: (REPORT_PAGE_SIZES as readonly number[]).includes(pageSize) ? pageSize : DEFAULT_REPORT_PAGE_SIZE,
  }
}

/** Returns the from>to error message, or undefined when the range is usable. */
export function rangeError(fromDate: string, toDate: string): string | undefined {
  return fromDate && toDate && fromDate > toDate ? 'From date must be on or before To date.' : undefined
}

function toSearchParams(v: ReportFilterValues): URLSearchParams {
  const sp = new URLSearchParams()
  for (const [k, val] of Object.entries(v)) {
    if (val !== '' && val !== DEFAULTS[k as keyof ReportFilterValues]) sp.set(k, String(val))
  }
  return sp
}

export interface UseReportFiltersReturn {
  values: ReportFilterValues
  /** Server params: only set, valid filters. Undefined while the range is invalid. */
  query: DeviceLockUnlockFilters | undefined
  error: string | undefined
  /** True when any filter (not sort/page size) differs from its default. */
  hasActiveFilters: boolean
  /** Changes whenever the result set changes; pagination resets on it. */
  resetKey: string
  setFilters: (patch: ReportFilterPatch) => void
  setPageSize: (size: number) => void
  reset: () => void
}

export function useReportFilters({ lockUnlock = false }: { lockUnlock?: boolean } = {}): UseReportFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams()
  const values = useMemo(() => parseReportFilters(searchParams, lockUnlock), [searchParams, lockUnlock])
  const error = rangeError(values.fromDate, values.toDate)

  const query = useMemo<DeviceLockUnlockFilters | undefined>(() => {
    if (error) return undefined
    const q: DeviceLockUnlockFilters = { sortBy: values.sortBy, sortOrder: values.sortOrder }
    if (values.fromDate) q.fromDate = values.fromDate
    if (values.toDate) q.toDate = values.toDate
    if (values.deviceId) q.deviceId = values.deviceId
    if (values.action) q.action = values.action
    if (values.operator) q.operator = values.operator
    return q
  }, [values, error])

  const write = useCallback(
    (next: ReportFilterValues) => setSearchParams(toSearchParams(next), { replace: true }),
    [setSearchParams],
  )

  const setFilters = useCallback(
    (patch: ReportFilterPatch) => {
      const trimmed = Object.fromEntries(
        Object.entries(patch).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v]),
      ) as ReportFilterPatch
      write({ ...values, ...trimmed })
    },
    [values, write],
  )

  const setPageSize = useCallback((pageSize: number) => write({ ...values, pageSize }), [values, write])

  // Reset clears filters and sort but keeps the user's chosen page size.
  const reset = useCallback(() => write({ ...DEFAULTS, pageSize: values.pageSize }), [values.pageSize, write])

  return {
    values,
    query,
    error,
    hasActiveFilters: !!(values.fromDate || values.toDate || values.deviceId || values.action || values.operator),
    resetKey: toSearchParams(values).toString(),
    setFilters,
    setPageSize,
    reset,
  }
}
